// src/pages/StaffPages/PositionPanel/Warehouse.jsx
import React, { useEffect, useState } from "react";
import styles from "./Warehouse.module.css";
import { API_URL } from "../../../config/api";

// ✅ Константы вне компонента, чтобы ESLint не требовал их в deps
const INITIAL_PERMISSIONS = [
  { name: "Просмотр остатков (мобильное приложение)", columns: ["view"] },
  { name: "Ревизия", columns: ["view", "create", "edit", "delete"] },
  { name: "Приёмка", columns: ["view", "create", "edit", "delete"] },
  { name: "Счет на оплату", columns: ["view", "create", "edit"] },
  { name: "Номенклатура", columns: ["view", "create", "edit", "delete"] },
  { name: "Склады", columns: ["view", "create", "edit", "delete"] },
  { name: "Перемещение", columns: ["view", "create", "edit"] },
  { name: "Списания", columns: ["view", "create"] },
];

const KEY_MAP = {
  nomenclature: "Номенклатура",
  storages: "Склады",
  receival: "Приёмка",
  writeoff: "Списания",
  transfer: "Перемещение",
  revision: "Ревизия",
  invoice: "Счет на оплату",
  balances: "Просмотр остатков (мобильное приложение)",
};

const Warehouse = ({ roleId, roleTitle }) => {
  const token = localStorage.getItem("token");
  const [permissions, setPermissions] = useState({});
  const [terminalAccess, setTerminalAccess] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!roleId) {
      setRoleName("");
      const emptyPerms = {};
      INITIAL_PERMISSIONS.forEach((item) => {
        emptyPerms[item.name] = { view: false, create: false, edit: false, delete: false };
      });
      setPermissions(emptyPerms);
      setTerminalAccess(false);
      return;
    }

    setRoleName(roleTitle || "");

    const fetchPermissions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/roles/${roleId}/acls?category=stocks`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (Array.isArray(data.data)) {
          const permsObj = {};
          INITIAL_PERMISSIONS.forEach((item) => {
            const permData = data.data.find((d) => KEY_MAP[d.key] === item.name);
            permsObj[item.name] = {
              view: permData?.read || false,
              create: permData?.create || false,
              edit: permData?.update || false,
              delete: permData?.delete || false,
            };
          });
          setPermissions(permsObj);
        }
      } catch (err) {
        console.error("Ошибка при получении прав:", err);
      }
    };

    fetchPermissions();
  }, [roleId, roleTitle, token]);

  const handleCheckboxChange = (row, key) => {
    setPermissions((prev) => ({
      ...prev,
      [row]: { ...prev[row], [key]: !prev[row]?.[key] },
    }));
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 3000);
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      showToast("Введите название роли!");
      return;
    }

    try {
      if (roleId && roleName !== roleTitle) {
        const resTitle = await fetch(`${API_URL}/api/roles/${roleId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ title: roleName }),
        });
        if (!resTitle.ok) {
          const data = await resTitle.json();
          showToast(`Ошибка при изменении названия: ${data.errors?.message || "Неизвестная ошибка"}`);
          return;
        }
      }

      const items = INITIAL_PERMISSIONS.map((item) => {
        const perms = permissions[item.name];
        const key = Object.keys(KEY_MAP).find((k) => KEY_MAP[k] === item.name);
        let type = 0;
        if (perms?.view) type += 1;
        if (perms?.create) type += 2;
        if (perms?.edit) type += 4;
        if (perms?.delete) type += 8;

        return {
          category: "stocks",
          key,
          type,
          read: !!perms?.view,
          create: !!perms?.create,
          update: !!perms?.edit,
          delete: !!perms?.delete,
          id: null,
        };
      });

      let res;
      if (roleId) {
        res = await fetch(`${API_URL}/api/roles/${roleId}/acls/bulk_load`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ items }),
        });
      } else {
        const payload = { only_terminal: terminalAccess, title: roleName, type: "cashier", items };
        res = await fetch(`${API_URL}/api/roles`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json();
      if (!res.ok) {
        showToast(`Ошибка: ${data.errors?.message || "Неизвестная ошибка"}`);
        return;
      }

      showToast(roleId ? "Роль успешно обновлена!" : "Роль успешно создана!");
    } catch (err) {
      console.error("Ошибка при сохранении:", err);
      showToast(`Ошибка: ${err.message}`);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <label>Название роли</label>
        <input
          type="text"
          className={styles.roleInput}
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          placeholder="Введите название роли"
        />
        <div className={styles.terminalWrapper}>
          <label className={styles.terminalLabel}>
            <label className={styles.checkboxContainer}>
              <input
                type="checkbox"
                checked={terminalAccess}
                onChange={() => setTerminalAccess(!terminalAccess)}
              />
              <span className={styles.customCheckbox}></span>
            </label>
            <span className={styles.text}>Доступ только в терминал</span>
          </label>
        </div>
      </div>

      <table className={styles.permissionTable}>
        <thead>
          <tr>
            <th>Название</th>
            <th>Просмотр</th>
            <th>Создать</th>
            <th>Изменить</th>
            <th>Удалить</th>
          </tr>
        </thead>
        <tbody>
          {INITIAL_PERMISSIONS.map((item) => (
            <tr key={item.name}>
              <td>{item.name}</td>
              {["view", "create", "edit", "delete"].map((col) => (
                <td key={col}>
                  {item.columns.includes(col) && (
                    <label className={styles.checkboxContainer}>
                      <input
                        type="checkbox"
                        checked={permissions[item.name]?.[col] || false}
                        onChange={() => handleCheckboxChange(item.name, col)}
                      />
                      <span className={styles.customCheckbox}></span>
                    </label>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <button className={styles.saveButton} onClick={handleSave}>
        Сохранить
      </button>

      {toast && <div className={styles.toast}>{toast}</div>}
    </div>
  );
};

export default Warehouse;
